import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { api, ApiError, call } from "../api/client";
import type { ConnectRequestView } from "../api/types";
import { AuthGate, useCurrentUser } from "./AuthPages";

/** Explanations for the reasons the server gives when it turns an application away. */
const reasons: Record<string, string> = {
  "nicht-zugelassen": "Diese Anwendung steht nicht auf der Liste der zugelassenen Anwendungen. Ein Admin kann sie unter „Verwaltung → Anwendungen“ zulassen.",
  "client-id": "Die Anwendung hat sich mit einer ungültigen Adresse gemeldet.",
  "nicht-erreichbar": "Die Angaben der Anwendung ließen sich nicht laden. Bitte später noch einmal versuchen.",
  metadaten: "Die Angaben der Anwendung sind unvollständig oder ungültig.",
  "client-id-abweichend": "Die Angaben der Anwendung passen nicht zu ihrer Adresse.",
  callback: "Die Anwendung wollte an eine Adresse zurückkehren, die sie selbst nicht angibt.",
};

const errors: Record<string, string> = {
  invalid_request: "Die Anfrage der Anwendung ist ungültig.",
  invalid_client: "Die Anwendung ist unbekannt oder nicht zugelassen.",
  invalid_scope: "Die Anwendung hat Rechte angefragt, die es nicht gibt.",
  invalid_target: "Die Anwendung wollte auf einen unbekannten Dienst zugreifen.",
};

/**
 * Consent page of the authorization endpoint. The request of the application arrives here with its parameters;
 * the decision goes back to the endpoint as a form post from this site, so the sign-in cookie is sent with it.
 */
export function ConnectPage() {
  const [params] = useSearchParams();
  const error = params.get("fehler");
  if (error) {
    const reason = params.get("grund");
    return (
      <main className="auth">
        <div className="card auth-card">
          <h1>Verbinden nicht möglich</h1>
          <p className="error" role="alert">
            {(reason && reasons[reason]) ?? errors[error] ?? "Die Anfrage der Anwendung wurde abgelehnt."}
          </p>
          <p className="muted small">Fehlercode: {error}</p>
        </div>
      </main>
    );
  }

  return (
    <AuthGate>
      <Consent params={params} />
    </AuthGate>
  );
}

function Consent({ params }: { params: URLSearchParams }) {
  const user = useCurrentUser();
  const clientId = params.get("client_id") ?? "";
  const redirectUri = params.get("redirect_uri") ?? undefined;
  const scope = params.get("scope") ?? undefined;
  const request = useQuery({
    queryKey: ["oauth-request", params.toString()],
    queryFn: () =>
      call<ConnectRequestView>(api.GET("/api/oauth/request", { params: { query: { client_id: clientId, redirect_uri: redirectUri, scope } } })),
    retry: false,
  });

  return (
    <main className="auth">
      <div className="card auth-card">
        <h1>Anwendung verbinden</h1>
        {request.isLoading && <p className="muted">Lade …</p>}
        {request.error && (
          <p className="error" role="alert">
            {request.error instanceof ApiError ? request.error.message : String(request.error)}
          </p>
        )}
        {request.data && (
          <form method="post" action="/oauth/authorize" className="stack">
            <p>
              <strong>{request.data.applicationName}</strong> ({request.data.clientHost}) möchte auf dein Konto <strong>{user.displayName}</strong>{" "}
              zugreifen.
            </p>
            <div>
              <p className="muted small">Die Anwendung darf:</p>
              <ul className="consent-scopes">
                {request.data.scopes.map((s) =>
                  s.optional ? (
                    <li key={s.name} className="consent-optional">
                      <label>
                        <input type="checkbox" name="rechte" value={s.name} defaultChecked /> {s.label}
                      </label>
                    </li>
                  ) : (
                    <li key={s.name}>{s.label}</li>
                  ),
                )}
              </ul>
              {request.data.scopes.some((s) => s.optional) && (
                <p className="muted small">Was du abwählst, darf die Anwendung nicht. Ändern kannst du das nur, indem du neu verbindest.</p>
              )}
            </div>
            <p className="muted small">
              Nach deiner Entscheidung geht es zurück zu <strong>{request.data.redirectHost}</strong>.
            </p>
            {request.data.loopbackOnly && (
              <p className="notice warning-box small">
                Die Anwendung läuft auf diesem Gerät. Stimme nur zu, wenn du die Verbindung gerade selbst gestartet hast.
              </p>
            )}
            <p className="muted small">Du kannst die Verbindung jederzeit im Profil unter „Verbundene Anwendungen“ trennen.</p>
            {/* The choice of scopes comes only from the boxes above, never from the address. */}
            {[...params.entries()].filter(([name]) => name !== "rechte").map(([name, value], i) => (
              <input key={`${name}-${i}`} type="hidden" name={name} value={value} />
            ))}
            <div className="actions">
              <button type="submit" name="entscheidung" value="zustimmen" className="button primary">
                Zustimmen
              </button>
              <button type="submit" name="entscheidung" value="ablehnen" className="button secondary">
                Ablehnen
              </button>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}
